import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Upload, Link as LinkIcon, Video, CheckCircle2, X, Film, Clock3, LayoutGrid, Search } from "lucide-react";

/**
 * Vimeo Video Picker — two paths in one UI:
 *  1) "Importar por URL/ID" → POST /admin/vimeo/import  (no size limit; you upload manually on vimeo.com first)
 *  2) "Enviar arquivo agora" → POST /admin/vimeo/upload (up to ~500 MB)
 *
 * Props:
 *  - value: current Vimeo embed URL (or any string)
 *  - onChange(url, meta?): called after pick. `meta` includes id/name/duration/thumbnail
 *  - testId
 */
function formatDuration(seconds) {
    if (!seconds) return "";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

function LibraryModal({ open, onClose, onPick, testId }) {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = async (p = 1, q = "") => {
        setLoading(true); setError("");
        try {
            const { data } = await api.get(`/admin/vimeo/library?page=${p}&q=${encodeURIComponent(q)}`);
            setItems(data.items || []);
            setPage(data.page || p);
            setTotal(data.total || 0);
        } catch (e) { setError(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (open) load(1, "");
    }, [open]);

    if (!open) return null;
    const totalPages = Math.max(1, Math.ceil(total / 24));

    return (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testId}>
            <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
                <header className="px-6 py-5 border-b border-black/5 flex items-center justify-between gap-4">
                    <div>
                        <div className="overline text-[#5C5C5C]">Vimeo</div>
                        <div className="font-display text-2xl tracking-tight">Sua biblioteca de vídeos</div>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-black/5" data-testid={`${testId}-close`}>
                        <X className="w-5 h-5" />
                    </button>
                </header>
                <div className="px-6 py-4 border-b border-black/5 flex items-center gap-3">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 text-[#5C5C5C] absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                            data-testid={`${testId}-search`}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") load(1, query); }}
                            placeholder="Buscar por título..."
                            className="imq-input !pl-11"
                        />
                    </div>
                    <button type="button" onClick={() => load(1, query)} className="btn-primary whitespace-nowrap" data-testid={`${testId}-search-submit`}>
                        Buscar
                    </button>
                    <div className="text-xs font-body text-[#5C5C5C] whitespace-nowrap">{total} vídeo(s)</div>
                </div>

                <div className="flex-1 overflow-auto p-6">
                    {loading && (
                        <div className="flex items-center justify-center py-16 text-[#5C5C5C]">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...
                        </div>
                    )}
                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg" data-testid={`${testId}-error`}>{error}</div>
                    )}
                    {!loading && !error && items.length === 0 && (
                        <div className="text-center py-16 font-body text-[#5C5C5C]">
                            <Film className="w-7 h-7 mx-auto mb-3 text-[#9A9A9A]" />
                            Nenhum vídeo encontrado na sua conta Vimeo.
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {items.map((v) => (
                                <button
                                    type="button"
                                    key={v.id}
                                    data-testid={`${testId}-item-${v.id}`}
                                    onClick={() => { onPick(v); onClose(); }}
                                    className="group bg-white rounded-2xl border border-black/5 overflow-hidden text-left hover:border-[#00D4D4]/60 hover:-translate-y-0.5 transition-all"
                                >
                                    <div className="relative aspect-video bg-[#1A1A1A] overflow-hidden">
                                        {v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover" loading="lazy" />}
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                                            <CheckCircle2 className="w-9 h-9 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                        {v.duration > 0 && (
                                            <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/70 text-white text-[11px] font-display">{formatDuration(v.duration)}</div>
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <div className="font-display text-sm tracking-tight line-clamp-2">{v.name || "(sem título)"}</div>
                                        <div className="overline text-[#5C5C5C] mt-1">ID {v.id}{v.status && v.status !== "available" ? ` · ${v.status}` : ""}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {totalPages > 1 && (
                    <footer className="px-6 py-3 border-t border-black/5 flex items-center justify-between text-xs font-body text-[#5C5C5C]">
                        <span>Página {page} de {totalPages}</span>
                        <div className="flex gap-2">
                            <button disabled={page <= 1 || loading} onClick={() => load(page - 1, query)} className="btn-ghost text-xs disabled:opacity-40">Anterior</button>
                            <button disabled={page >= totalPages || loading} onClick={() => load(page + 1, query)} className="btn-ghost text-xs disabled:opacity-40">Próxima</button>
                        </div>
                    </footer>
                )}
            </div>
        </div>
    );
}

export default function VimeoPicker({ value, onChange, testId = "vimeo-picker" }) {
    const [mode, setMode] = useState("url"); // "url" | "upload"
    const [urlInput, setUrlInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [preview, setPreview] = useState(null);
    const [libOpen, setLibOpen] = useState(false);
    const [progress, setProgress] = useState(0);
    const fileRef = useRef(null);

    const setResult = (meta) => {
        setPreview(meta);
        onChange(meta.embed_url || meta.link, meta);
    };

    const importByUrl = async () => {
        if (!urlInput.trim()) return;
        setError(""); setLoading(true);
        try {
            const { data } = await api.post("/admin/vimeo/import", { url: urlInput.trim() });
            setResult(data);
        } catch (e) { setError(formatApiError(e)); }
        finally { setLoading(false); }
    };

    const uploadFile = async (file) => {
        if (!file) return;
        if (!/^video\//.test(file.type) && !/\.(mp4|mov|m4v|webm)$/i.test(file.name)) {
            setError("Selecione um arquivo de vídeo (MP4, MOV, M4V ou WebM).");
            return;
        }
        if (file.size > 20 * 1024 * 1024 * 1024) {
            setError("Arquivo muito grande (máx 20 GB).");
            return;
        }
        setError(""); setLoading(true); setProgress(0);
        try {
            // 1) Ask backend to create a Vimeo TUS upload session
            const { data: session } = await api.post("/admin/vimeo/create-session", {
                name: file.name.replace(/\.[^.]+$/, ""),
                size: file.size,
            });

            // 2) Upload chunks directly from browser → Vimeo (bypasses the proxy entirely)
            const CHUNK = 50 * 1024 * 1024; // 50 MB chunks
            let offset = 0;
            while (offset < file.size) {
                const end = Math.min(offset + CHUNK, file.size);
                const chunk = file.slice(offset, end);
                const r = await fetch(session.upload_link, {
                    method: "PATCH",
                    headers: {
                        "Tus-Resumable": "1.0.0",
                        "Upload-Offset": String(offset),
                        "Content-Type": "application/offset+octet-stream",
                    },
                    body: chunk,
                });
                if (!r.ok && r.status !== 204 && r.status !== 200) {
                    throw new Error(`Upload falhou no offset ${offset}: HTTP ${r.status}`);
                }
                const newOffset = parseInt(r.headers.get("Upload-Offset") || String(end), 10);
                offset = newOffset > offset ? newOffset : end;
                setProgress(Math.round((offset / file.size) * 100));
            }

            // 3) Ask backend to fetch metadata (link, embed_url, thumbnail) by ID
            const { data: meta } = await api.post("/admin/vimeo/import", { url: session.id });
            setResult(meta);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || "Falha no upload.");
        } finally {
            setLoading(false);
            setProgress(0);
        }
    };

    const clear = () => {
        setPreview(null);
        setUrlInput("");
        onChange("", null);
    };

    if (value && !preview) {
        // existing value passed in, show as compact card
        return (
            <>
                <div data-testid={`${testId}-existing`} className="flex items-center gap-3 bg-white border border-black/10 rounded-xl px-3 py-2.5">
                    <Video className="w-4 h-4 text-[#1A1A1A]" />
                    <a href={value} target="_blank" rel="noreferrer" className="font-body text-xs text-[#1A1A1A] truncate flex-1 hover:underline">{value}</a>
                    <button type="button" onClick={() => setLibOpen(true)} className="text-xs font-display text-[#5C5C5C] hover:text-[#1A1A1A] inline-flex items-center gap-1" title="Trocar da biblioteca">
                        <LayoutGrid className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={clear} className="text-red-600 hover:text-red-700" data-testid={`${testId}-clear`}>
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <LibraryModal open={libOpen} onClose={() => setLibOpen(false)} onPick={setResult} testId={`${testId}-library`} />
            </>
        );
    }

    if (preview) {
        return (
            <>
                <div data-testid={`${testId}-preview`} className="bg-white border border-[#00D4D4]/40 rounded-2xl p-4 flex gap-4">
                    {preview.thumbnail && <img src={preview.thumbnail} alt="" className="w-32 h-20 rounded-lg object-cover" />}
                    <div className="flex-1 min-w-0">
                        <div className="inline-flex items-center gap-1.5 text-[#00D4D4] text-[10px] tracking-widest font-display uppercase">
                            <CheckCircle2 className="w-3 h-3" /> Vimeo conectado
                        </div>
                        <div className="font-display text-sm tracking-tight mt-1 truncate">{preview.name}</div>
                        <div className="flex items-center gap-3 text-[11px] font-body text-[#5C5C5C] mt-1">
                            <span>ID: {preview.id}</span>
                            {preview.duration ? <span className="inline-flex items-center gap-1"><Clock3 className="w-3 h-3" /> {formatDuration(preview.duration)}</span> : null}
                            {preview.status && <span>· {preview.status === "available" ? "pronto" : preview.status}</span>}
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5 self-start">
                        <button type="button" onClick={() => setLibOpen(true)} className="p-1.5 rounded-lg hover:bg-black/5" title="Trocar da biblioteca">
                            <LayoutGrid className="w-4 h-4 text-[#5C5C5C]" />
                        </button>
                        <button type="button" onClick={clear} className="text-red-600 hover:text-red-700 p-1.5" data-testid={`${testId}-clear`}>
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <LibraryModal open={libOpen} onClose={() => setLibOpen(false)} onPick={setResult} testId={`${testId}-library`} />
            </>
        );
    }

    return (
        <div data-testid={testId} className="space-y-3">
            {/* Mode toggle + library shortcut */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex bg-[#FAFAF7] border border-black/5 rounded-full p-0.5 text-xs font-display">
                    <button type="button" onClick={() => setMode("url")} data-testid={`${testId}-tab-url`} className={`px-3 py-1.5 rounded-full transition-colors ${mode === "url" ? "bg-[#1A1A1A] text-white" : "text-[#5C5C5C]"}`}>
                        <LinkIcon className="w-3 h-3 inline mr-1" /> Importar por URL
                    </button>
                    <button type="button" onClick={() => setMode("upload")} data-testid={`${testId}-tab-upload`} className={`px-3 py-1.5 rounded-full transition-colors ${mode === "upload" ? "bg-[#1A1A1A] text-white" : "text-[#5C5C5C]"}`}>
                        <Upload className="w-3 h-3 inline mr-1" /> Enviar agora
                    </button>
                </div>
                <button type="button" onClick={() => setLibOpen(true)} data-testid={`${testId}-open-library`} className="ml-auto text-xs font-display text-[#1A1A1A] hover:text-holo inline-flex items-center gap-1.5">
                    <LayoutGrid className="w-3.5 h-3.5" /> Escolher da minha biblioteca
                </button>
            </div>

            {mode === "url" ? (
                <div className="flex gap-2">
                    <input
                        data-testid={`${testId}-url-input`}
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        placeholder="Cole a URL do Vimeo (ex.: https://vimeo.com/12345678) ou apenas o ID"
                        className="imq-input flex-1"
                    />
                    <button type="button" onClick={importByUrl} disabled={loading || !urlInput.trim()} data-testid={`${testId}-url-submit`} className="btn-primary whitespace-nowrap">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />} Buscar
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={loading}
                    data-testid={`${testId}-upload-pick`}
                    className="w-full px-4 py-6 rounded-2xl border-2 border-dashed border-black/15 hover:border-black/30 bg-[#FAFAF7] hover:bg-white flex flex-col items-center gap-2 transition-colors"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-6 h-6 text-[#5C5C5C] animate-spin" />
                            <div className="font-display text-sm text-[#5C5C5C]">Enviando para Vimeo...</div>
                            <div className="font-body text-[11px] text-[#5C5C5C]">Pode levar alguns minutos.</div>
                        </>
                    ) : (
                        <>
                            <div className="w-11 h-11 rounded-full bg-white border border-black/5 flex items-center justify-center">
                                <Upload className="w-5 h-5 text-[#5C5C5C]" />
                            </div>
                            <div className="font-display text-sm">Selecionar vídeo do computador</div>
                            <div className="font-body text-[11px] text-[#5C5C5C]">MP4, MOV, M4V ou WebM · até 500 MB</div>
                        </>
                    )}
                </button>
            )}

            <input
                ref={fileRef}
                type="file"
                accept="video/mp4,video/quicktime,video/x-m4v,video/webm,.mp4,.mov,.m4v,.webm"
                className="hidden"
                data-testid={`${testId}-file-input`}
                onChange={(e) => uploadFile(e.target.files?.[0])}
            />

            {error && (
                <div data-testid={`${testId}-error`} className="text-xs text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>
            )}

            <div className="text-[11px] font-body text-[#5C5C5C] flex items-start gap-1.5">
                <Film className="w-3 h-3 mt-0.5 shrink-0" />
                <span>Para aulas grandes (&gt; 500 MB), envie manualmente em <a className="underline" href="https://vimeo.com/manage/videos" target="_blank" rel="noreferrer">vimeo.com</a> e use "Importar por URL" — sem limite de tamanho.</span>
            </div>

            <LibraryModal open={libOpen} onClose={() => setLibOpen(false)} onPick={setResult} testId={`${testId}-library`} />
        </div>
    );
}
